import React from 'react';
import EventItem from './EventItem';
import { RsvpResponse } from '../../types';

interface Event {
  id: number;
  name: string;
  description?: string;
  date?: string;
  order: number;
}

interface GuestInfo {
  id: number;
  name: string;
}

interface RsvpFormProps {
  guests: GuestInfo[];
  events: Event[];
  responses: Map<number, RsvpResponse>;
  guestMessages: Map<number, string>;
  onResponseChange: (eventId: number, attending: boolean) => void;
  onMessageChange: (guestId: number, message: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
  loading: boolean;
  error?: string;
}

const formatDate = (dateString?: string): string | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
};

const RsvpForm: React.FC<RsvpFormProps> = ({
  guests,
  events,
  responses,
  guestMessages,
  onResponseChange,
  onMessageChange,
  onSubmit,
  onReset,
  loading,
  error,
}) => {
  const formatGuestNames = (guestList: GuestInfo[]): string => {
    const names = guestList.map(g => g.name);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} et ${names[1]}`;
    const lastGuest = names[names.length - 1];
    const otherGuests = names.slice(0, -1).join(', ');
    return `${otherGuests} et ${lastGuest}`;
  };

  return (
    <div className="rsvp-card">
      <div className="card-header">
        <h2>RSVP pour {formatGuestNames(guests)}</h2>
        <p className="card-subtitle">Merci de répondre pour chaque événement</p>
      </div>

      <form onSubmit={onSubmit}>
        <div className="events-list">
          {events.map((event) => (
            <EventItem
              key={event.id}
              eventId={event.id}
              eventName={event.name}
              eventDescription={event.description}
              eventDate={formatDate(event.date)}
              attending={responses.get(event.id)?.attending}
              onResponseChange={onResponseChange}
            />
          ))}
        </div>

        <div className="guest-messages-section">
          <h3 className="messages-title">Messages individuels (optionnel)</h3>
          <p className="messages-subtitle">Allergies, besoins particuliers, etc.</p>

          {guests.map((guest) => (
            <div key={guest.id} className="guest-message-item">
              <label htmlFor={`message-${guest.id}`} className="guest-message-label">
                {guest.name}
              </label>
              <textarea
                id={`message-${guest.id}`}
                value={guestMessages.get(guest.id) || ''}
                onChange={(e) => onMessageChange(guest.id, e.target.value)}
                placeholder="Allergies, besoins particuliers..."
                rows={3}
                className="modern-textarea"
              />
            </div>
          ))}
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <div className="button-group">
          <button type="button" onClick={onReset} className="modern-btn secondary">
            Recommencer
          </button>
          <button type="submit" disabled={loading} className="modern-btn primary">
            {loading ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RsvpForm;
